import { useMemo } from "react";
import { useACLContext, type CheckOptions } from "../context";
import type {
  Action,
  Environment,
  EvaluationResult,
  Resource,
  Scope,
} from "@shield-acl/core";
import { stableKey } from "./internal";

export interface ResourceBinding<TRecord> {
  resource: Resource;
  record: TRecord;
  can: (action: Action, extra?: Omit<CheckOptions, "record">) => boolean;
  evaluate: (action: Action) => EvaluationResult;
  canRead: () => boolean;
  canCreate: () => boolean;
  canUpdate: () => boolean;
  canDelete: () => boolean;
  canManage: () => boolean;
}

/**
 * Vincula tipo + instância do recurso: a instância entra automaticamente nas
 * conditions (via `record`). Substitui usePermissionHelpers/useResourceACL.
 *
 * @example
 * const acl = useResource("posts", post)
 * acl.canUpdate()          acl.can("publish")
 * acl.can("read", { scope: "app:B" })
 */
export function useResource<TRecord>(
  type: Resource,
  record: TRecord,
  opts?: { scope?: Scope; environment?: Environment },
): ResourceBinding<TRecord> {
  const { can, evaluate, user, revision } = useACLContext();
  const scope = opts?.scope;
  const environment = opts?.environment;

  return useMemo(() => {
    const base: CheckOptions<TRecord> = { record, scope, environment };
    const check = (action: Action, extra?: Omit<CheckOptions, "record">) =>
      can(action, type, { ...base, ...extra });

    return {
      resource: type,
      record,
      can: check,
      evaluate: (action: Action) => evaluate(action, type, base),
      canRead: () => check("read") || check(`${type}.read`),
      canCreate: () => check("create") || check(`${type}.create`),
      canUpdate: () => check("update") || check(`${type}.update`),
      canDelete: () => check("delete") || check(`${type}.delete`),
      canManage: () => check("manage") || check("*"),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    can,
    evaluate,
    type,
    stableKey(record),
    scope,
    stableKey(environment),
    user,
    revision,
  ]);
}
